import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { GzTreeWebComponent } from '../../ui/gzTree';
import { GzSingleSelectListWebComponent } from '../../ui/gzSingleSelectList';
import { GzDropdownWebComponent } from '../../ui/gzDropdown';
import { toggleVisibility } from '../../utils/htmlElement.utils';
import { DynamicTreeDataSource, NodeData, NodeOptions, SelectedNodes, TreeEvent } from '../../ui/gzTree/type';
import _ from 'lodash';
import { WebComponentEvent } from '../../framework/BaseWebComponentController';
import { AvailableService, Node, SelectedTargets, SERVICE_TARGETS, ServiceTargetId, Target, TargetsViewData, } from '../type';
import { GzTitleWebComponent } from '../../ui/gzTitle';
import { SelectOption } from '../../ui/gzDropdown/types';
import { LabelWidthValues, TemplateState } from '../../types/common/types';
import { GzInfoBoxWebComponent } from '../../ui/gzInfoBox';
import { FormGroup } from '../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../ui/gzForm/validators/required.validator';
import { GzFormFieldWebComponent } from '../../ui/gzForm/formField';
import { translateProtectedEntity } from '../helpers/translateProtectedEntity';
import { Companies } from '@nirvana/shared/constants';
import { translateFn } from '../../utils/translator.helper';

export class TargetsView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $selectTargetsTitle: GzTitleWebComponent;
    $targetsTree: GzTreeWebComponent<any>;
    $searchListGroups: GzSingleSelectListWebComponent;
    $serviceTargetsDropDown: GzDropdownWebComponent;
    $selectedTargetsWrapper: HTMLElement;
    $targetsTreeWrapper: HTMLElement;
    $selectedTargetsTitle: GzTitleWebComponent;
    $noTargetsSelectedError: GzInfoBoxWebComponent;
    $atLeastOneTargetSelected: GzFormFieldWebComponent;
  };

  public selectedTargets: { [serviceTargetId: number]: SelectedNodes<any>[] };
  public initialSelectedTargets: string[][] = [];
  public currentSelectedTargets: string[][] = [];
  public formGroup: FormGroup;
  public rootNode: NodeOptions;
  public currentServiceTargetId: ServiceTargetId;
  private availableServices: Array<AvailableService>;
  public companyId: string;

  private static getTargetsStrings(): any {
    return {
      defaultSelectTargetsTitle: translateFn('Select Targets'),
      defaultSelectedTargetsTitle: translateFn('Selected Groups'),
      noTargetsSelectedError: translateFn('You must select at least one target'),
      searchPlaceholder: translateFn('Search'),
    };
  }

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $selectTargetsTitle: this.shadow.querySelector('#targets-title'),
      $targetsTree: new GzTreeWebComponent(),
      $searchListGroups: this.shadow.querySelector('#group-search'),
      $serviceTargetsDropDown: this.shadow.querySelector('#service-targets-drop-down'),
      $selectedTargetsWrapper: this.shadow.querySelector('.selected-targets-wrapper'),
      $targetsTreeWrapper: this.shadow.querySelector('#targets-tree-wrapper'),
      $selectedTargetsTitle: this.shadow.querySelector('#selected-targets-title'),
      $noTargetsSelectedError: this.shadow.querySelector('#no-targets-selected-error'),
      $atLeastOneTargetSelected: this.shadow.querySelector('#at-least-one-target-selected'),
    };
    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.translations = TargetsView.getTargetsStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$selectTargetsTitle.innerText = this.translations.defaultSelectTargetsTitle;
    this.dom.$selectedTargetsTitle.innerText = this.translations.defaultSelectedTargetsTitle;
    this.dom.$noTargetsSelectedError.innerText = this.translations.noTargetsSelectedError;
    this.dom.$searchListGroups.searchPlaceholderString = this.translations.searchPlaceholder;
  }

  disconnected() {}

  private createFormGroup() {
    this.formGroup = new FormGroup({
      $atLeastOneTargetSelected: this.dom.$atLeastOneTargetSelected,
    });

    this.setRequiredValidators();
  }

  public setRequiredValidators(): void {
    this.dom.$atLeastOneTargetSelected.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  public removeValidators(): void {
    if (this.dom.$atLeastOneTargetSelected) {
      this.dom.$atLeastOneTargetSelected.Validators = [];
    }
  }

  public hideSelectTargetsTitle() {
    toggleVisibility(this.dom.$selectTargetsTitle, false);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public async buildSuccessView(viewData: TargetsViewData): Promise<void> {
    this.createFormGroup();

    this.initData();

    if (!viewData?.companyId || !viewData?.availableServices || viewData?.availableServices.length === 0) {
      this.removeLoadingState();
      return;
    }

    this.currentServiceTargetId = viewData.availableServices[0].id;
    this.availableServices = viewData.availableServices;

    if (!this.dependencies.isCloud()) {
      this.buildServiceSelector();
    }

    if (viewData?.rootNode) {
      this.rootNode = viewData.rootNode;
    }

    if (viewData.treeDataSource) {
      this.setTreeData(viewData.treeDataSource);
      this.preSelectTreeNodes(viewData.preSelectedTargets);
    }

    if (viewData.isSingleSelect) {
      this.dom.$searchListGroups.setAttribute('class', 'single-select-list');
    }

    this.removeLoadingState();
  }

  public async buildErrorView(error: Error): Promise<void> {
    this.removeLoadingState();
  }

  private buildServiceSelector(): void {
    let preselectedOption;
    const serviceOptions = [] as Array<SelectOption>;
    this.availableServices.forEach(availableService => {
      Object.keys(SERVICE_TARGETS).forEach(key => {
        if (SERVICE_TARGETS[key].id === availableService.id) {
          const option = {
            id: SERVICE_TARGETS[key].id,
            title: SERVICE_TARGETS[key].name,
            value: key,
          };
          serviceOptions.push(option);

          if (SERVICE_TARGETS[key].id === this.currentServiceTargetId) {
            preselectedOption = option;
          }
        }
      });
    });

    this.dom.$serviceTargetsDropDown.borderless = false;
    this.dom.$serviceTargetsDropDown.labelWidth = LabelWidthValues.FULL;
    this.dom.$serviceTargetsDropDown.setAttribute('options', JSON.stringify(serviceOptions));
    if (preselectedOption) {
      this.dom.$serviceTargetsDropDown.setAttribute('value', preselectedOption.value as string);
    } else {
      this.dom.$serviceTargetsDropDown.setAttribute('value', serviceOptions[0].value as string);
    }
    toggleVisibility(this.dom.$serviceTargetsDropDown, true);
  }

  public preSelectTreeNodes(preselectedTargets?: SelectedTargets): void {
    if (!preselectedTargets || preselectedTargets.length === 0) {
      // preselect the root node if currentSelectedCompany !== logged-in user company
      if (this.dependencies.getUser().company.id !== this.companyId && this.rootNode) {
        this.selectRootNode(this.rootNode);
        this.toggleTargetsSelectedError();
        return;
      }

      // if there is no preSelectedTargets use the ones from current logged-in user
      preselectedTargets = this.dependencies.getUser().serviceTargets;
    }

    if (!preselectedTargets) {
      this.toggleTargetsSelectedError();
      return;
    }

    preselectedTargets.forEach(elem => {
      const serviceId = elem.serviceId !== 0 ? elem.serviceId : 1;
      this.selectedTargets[serviceId] = this.selectedTargets[serviceId].concat(
        elem.targets.map(target => {
          const listItem = {
            label: this.getNodeLabel(target),
            value: target.name,
            nodeId: target.protectedEntityId,
            nodeName: target.name,
            originalData: {
              path: target.path,
              type: target.type,
              company: target.company,
            },
            parentNodeId: target.company?.id,
            parentName: target.company?.name,
          };

          return {
            key: listItem.nodeId,
            value: listItem,
          };
        })
      );
    });

    this.dom.$targetsTree.selectedNodes = this.selectedTargets[this.currentServiceTargetId];
    this.setSearchListGroups(this.selectedTargets[this.currentServiceTargetId]);
    this.toggleTargetsSelectedError();
  }

  public setTreeData(treeDataSource: DynamicTreeDataSource): void {
    try {
      this.dom.$targetsTree.remove();
      this.dom.$targetsTree = new GzTreeWebComponent();

      this.dom.$targetsTree.dataSource = treeDataSource;
      this.dom.$targetsTree.setAttribute('id', 'targets-tree');
      this.dom.$targetsTreeWrapper.append(this.dom.$targetsTree);
      this.dom.$targetsTree.addEventListener(TreeEvent.NODE_CHECKED, (event: WebComponentEvent) => {
        this.selectNode(event.detail.value);
      });
    } finally {
      this.removeLoadingState();
    }
  }

  private selectNode(node: NodeData): void {
    if (this.dom.$searchListGroups.searchInput?.value) {
      this.dom.$searchListGroups.searchInput.clearInputValue(new Event('clear'));
    }

    this.dom.$targetsTree.selectedNodes = this.dom.$targetsTree.selectedNodes.flatMap(selectedNode => {
      const regex = new RegExp(node.nodeId, 'gi');
      return (selectedNode.value.originalData.path.match(regex) || []).length > 0 ? [] : selectedNode;
    });

    this.selectedTargets[this.currentServiceTargetId] = this.dom.$targetsTree.selectedNodes;
    this.setSearchListGroups(this.dom.$targetsTree.selectedNodes);

    this.toggleTargetsSelectedError();
  }

  public toggleTargetsSelectedError(): void {
    let atLeastOneTargetIsSelected = false;
    for (const serviceId in this.selectedTargets) {
      if (this.selectedTargets[serviceId].length > 0) {
        atLeastOneTargetIsSelected = true;
      }
    }

    this.dom.$atLeastOneTargetSelected.value = atLeastOneTargetIsSelected ? true : '';
    toggleVisibility(this.dom.$noTargetsSelectedError, !this.dom.$atLeastOneTargetSelected.value);
  }

  private selectRootNode(rootNode: NodeOptions): void {
    const rootNodeSelectedTarget = [
      {
        key: rootNode.currentNode.nodeId,
        value: rootNode.currentNode,
      },
    ];
    this.selectedTargets[this.currentServiceTargetId] = _.cloneDeep(rootNodeSelectedTarget);
    this.dom.$targetsTree.selectedNodes = this.selectedTargets[this.currentServiceTargetId];
    this.setSearchListGroups(this.selectedTargets[this.currentServiceTargetId]);
  }

  public getNodeLabel(node: Node | Target): string {
    const isCloud = this.dependencies.isCloud();

    if (!isCloud) {
      return translateProtectedEntity(node.type, node.name, isCloud);
    }

    if (node.type === Companies.TYPE_COMPANY && (!node?.parentId && !node.company)) {
      return node.name;
    }

    if (node.company) {
      return node.company.name + ' \\ ' + translateProtectedEntity(node.type, node.name, isCloud);
    }

    return translateProtectedEntity(node.type, node.name, isCloud);
  }

  private initData(): void {
    this.dom.$searchListGroups.filterOptions = [];
    this.dom.$targetsTree.selectedNodes = [];
    this.selectedTargets = {
      [SERVICE_TARGETS.SVE.id]: [] as SelectedNodes<any>[],
      [SERVICE_TARGETS.EPS.id]: [] as SelectedNodes<any>[],
      [SERVICE_TARGETS.MDM.id]: [] as SelectedNodes<any>[],
    };
  }

  public setSearchListGroups(source: SelectedNodes<any>[]) {
    this.dom.$searchListGroups.filterOptions = source.map(selectedTarget => selectedTarget.value);
    this.currentSelectedTargets[this.currentServiceTargetId] = source.map(
      selectedTarget => selectedTarget.value.nodeId
    );
    if (!this.initialSelectedTargets[this.currentServiceTargetId]) {
      this.initialSelectedTargets[this.currentServiceTargetId] =
        this.currentSelectedTargets[this.currentServiceTargetId];
    }
  }
}
